import React, { useState } from "react";
import { ModalConfirmation } from "@adsk/alloy-react-modal";
import {
  useNotifications,
  NOTIFICATION_STATUSES,
} from "@adsk/alloy-react-notification";
import Theme from "@adsk/alloy-react-theme";
import Button from "@adsk/alloy-react-button";
import { ConditionalTooltip } from "@adsk/acc-issues-common/components/primitive/ConditionalTooltip";
import { Issue } from "@adsk/acc-issues-types";
import { useTranslations } from "@adsk/acc-issues-translations";
import { useShouldWarnBeforeUnpublish } from "../../hooks/useShouldWarnBeforeUnpublish";
import {
  CrownFilledIcon,
  CrownStrikethroughIcon,
} from "@adsk/alloy-react-icon";

export const PublishButton = ({
  issue,
  handleChange,
  disabled,
  tooltipContainer,
  onChangePublishStatus,
  shouldBeSkinny,
}: {
  issue: Issue;
  handleChange: (inputIssueChanges: any, { onSuccess, onFailure }: any) => void;
  disabled: boolean;
  tooltipContainer?: any;
  onChangePublishStatus?: (issueId: string, isNowPublished: boolean) => void;
  shouldBeSkinny?: boolean;
}) => {
  const { showNotification } = useNotifications();

  const [
    isUnpublishConfirmationModalOpen,
    setIsUnpublishConfirmationModalOpen,
  ] = useState(false);

  const { published } = issue;
  const { T } = useTranslations();
  const shouldWarn = useShouldWarnBeforeUnpublish([issue]);

  const buttonText = published
    ? T.translate("actions.unpublish")
    : T.translate("actions.publish");

  const isPublishedAfterClick = !published;

  const onPublishSuccess = () => {
    onChangePublishStatus &&
      onChangePublishStatus(issue.id, isPublishedAfterClick);
    showNotification({
      status: NOTIFICATION_STATUSES.SUCCESS,
      message: published
        ? T.translate("issueEditForm.unpublish.successMessage")
        : T.translate("issueEditForm.publish.successMessage"),
    });
  };

  const onPublishFailed = () => {
    showNotification({
      status: NOTIFICATION_STATUSES.ERROR,
      message: published
        ? T.translate("issueEditForm.unpublish.failureMessage")
        : T.translate("issueEditForm.publish.failureMessage"),
    });
  };

  return (
    <>
      <ConditionalTooltip
        condition={disabled}
        container={tooltipContainer}
        content={T.translate("noPermissions")}
      >
        <ConditionalTooltip
          // if the button should be skinny, we show a tooltip with the buttonText
          // as long as it's not disabled. If it's disabled, the other tooltip takes precedence.
          condition={Boolean(!disabled && shouldBeSkinny)}
          container={tooltipContainer}
          content={buttonText}
        >
          <Button
            data-testid="publish-button"
            disabled={disabled}
            style={
              disabled
                ? undefined
                : {
                    color: Theme.colors.adskBlue500,
                  }
            }
            variant={Button.VARIANTS.TERTIARY}
            renderIcon={p =>
              published ? (
                <CrownStrikethroughIcon {...p} />
              ) : (
                <CrownFilledIcon {...p} />
              )
            }
            onClick={() => {
              if (published) {
                if (shouldWarn) {
                  setIsUnpublishConfirmationModalOpen(true);
                } else {
                  handleChange(
                    { published: false },
                    { onSuccess: onPublishSuccess, onFailure: onPublishFailed }
                  );
                }
              } else {
                handleChange(
                  { published: true },
                  { onSuccess: onPublishSuccess, onFailure: onPublishFailed }
                );
              }
            }}
          >
            {
              // if the button is skinny, we only show the icon
            }
            {!shouldBeSkinny && buttonText}
          </Button>
        </ConditionalTooltip>
      </ConditionalTooltip>
      <ModalConfirmation
        style={{}}
        show={isUnpublishConfirmationModalOpen}
        title={T.translate("issueEditForm.publish.modalTitle") as string}
        message={T.translate("issueEditForm.publish.modalMessage")}
        confirmationButtonLabel={T.translate("actions.unpublish") as string}
        onConfirm={() => {
          handleChange(
            { published: false },
            { onSuccess: onPublishSuccess, onFailure: onPublishFailed }
          );
          setIsUnpublishConfirmationModalOpen(false);
        }}
        onClose={() => setIsUnpublishConfirmationModalOpen(false)}
      />
    </>
  );
};
